import { useMemo } from 'react';
import { Headset, User } from 'lucide-react';

import { ApiSchemas } from '@/components/providers/v1-api';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

type CallData = ApiSchemas['ExternalCallDetailOut'];
type Recording = NonNullable<CallData['recordings']>[number];

export interface AsrSentence {
  start: number;
  end: number;
  text?: string;
}

export interface AsrParagraph {
  start: number;
  end: number;
  speaker?: number;
  num_words?: number;
  sentences: AsrSentence[];
}

export interface AsrParagraphs {
  paragraphs: AsrParagraph[];
  transcript?: string;
}

export interface AsrAlternative {
  confidence: number;
  transcript: string;
  paragraphs: AsrParagraphs;
}

export interface AsrChannel {
  alternatives: AsrAlternative[];
  speaker_type?: 'customer' | 'agent';
}

export interface Asr {
  channels: AsrChannel[];
  summary?: string;
}

export function Summary({ recording }: { recording: Recording }) {
  const asrData = recording.asr_transcript_json as Asr | undefined;
  if (!asrData?.summary) {
    return null;
  }

  return (
    <div className='space-y-2'>
      <h3 className='text-heading-sm text-muted-foreground'>Summary</h3>
      <div className='text-sm'>{asrData.summary}</div>
    </div>
  );
}

interface TranscriptProps {
  recording: Recording;
  onClickStartAt?: (start: number) => void;
}

export function Transcript({ recording, onClickStartAt }: TranscriptProps) {
  const transcript = useMemo(() => getTranscriptItemsFromRecording(recording), [recording]);

  if (!transcript.length) {
    return (
      <div className='flex h-28 w-full items-center justify-center rounded-lg bg-muted/50 text-sm text-muted-foreground'>
        No transcript available
      </div>
    );
  }

  return (
    <div>
      <div className='px-6'>
        <h3 className='text-heading-md'>Transcript</h3>
        <Label>Auto-generated</Label>
      </div>
      <div className='mt-2 px-3'>
        {transcript.map((item, index) => (
          <ChatBubble message={item} key={index} onClick={() => onClickStartAt?.(item.start)} />
        ))}
      </div>
    </div>
  );
}

interface TranscriptItem {
  speakerId: string;
  speakerType: 'customer' | 'agent';
  start: number;
  end: number;
  text: string;
}

function ChatBubble({ message, onClick }: { message: TranscriptItem; onClick?: () => void }) {
  const bgColor = useMemo(() => {
    if (message.speakerType === 'customer') {
      return 'bg-primary/50';
    }
    const index = getIntFromString(message.speakerId, 2);
    return ['bg-muted shadow-sm', 'bg-muted/40 shadow-sm'][index];
  }, [message.speakerId, message.speakerType]);
  const time = useMemo(() => {
    const start = message.start;
    if (!start) return null;
    const minutes = Math.floor(start / 60);
    const seconds = Math.floor(start % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [message.start]);
  const Icon = message.speakerType === 'customer' ? User : Headset;

  return (
    <div
      className={cn(
        'flex w-full gap-3.5 rounded-lg p-3',
        onClick && 'cursor-pointer transition-colors hover:bg-muted/50'
      )}
      onClick={() => onClick?.()}
    >
      <div
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground',
          bgColor
        )}
      >
        <Icon className='size-4' />
      </div>
      <div className='space-y-0.5'>
        <div className='text-sm'>{message.text}</div>
        <div className='text-xs text-muted-foreground'>{time}</div>
      </div>
    </div>
  );
}

function getIntFromString(input: string, max: number): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash | 0; // Ensure 32-bit integer
  }
  return Math.abs(hash) % (max + 1);
}

/**
 * Creates a transcript from the recording.
 * Combines consecutive messages with the same speaker.
 * Trims whitespace from the start and end of the transcript.
 */
function getTranscriptItemsFromRecording(recording: Recording) {
  const asrData = recording.asr_transcript_json as { channels?: AsrChannel[] } | undefined;
  if (!asrData?.channels || asrData.channels.length === 0) {
    return [];
  }

  const channelsCombined: TranscriptItem[] = [];

  for (const [i, channel] of asrData.channels.entries()) {
    const speakerId = `channel-${i}`;
    const alternative = channel.alternatives?.[0];
    const paragraphs = alternative?.paragraphs?.paragraphs;
    if (!paragraphs) continue;

    for (const paragraph of paragraphs) {
      const sentences = paragraph.sentences;
      if (!sentences) continue;

      for (const sentence of sentences) {
        const speakerType = channel.speaker_type === 'customer' ? 'customer' : 'agent';
        const text = sentence.text?.trim();
        if (!text) continue;

        channelsCombined.push({
          speakerId,
          speakerType,
          start: sentence.start,
          end: sentence.end,
          text: text ?? '',
        });
      }
    }
  }

  // Sort by start time to get chronological order
  channelsCombined.sort((a, b) => a.start - b.start);

  // Combine consecutive messages with the same speaker
  const out: TranscriptItem[] = [];
  for (const item of channelsCombined) {
    const last = out[out.length - 1];
    if (last && last.speakerId === item.speakerId && last.speakerType === item.speakerType) {
      last.text += ` ${item.text}`;
    } else {
      out.push(item);
    }
  }
  return out;
}
